import { useId, type ReactNode } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { cx } from './cx';
import { FieldContext } from './field-context';

export interface FormFieldProps {
  label: ReactNode;
  children: ReactNode;
  /** Help text below the control (announced with it via aria-describedby). */
  hint?: ReactNode | undefined;
  /** Error message: marks the control aria-invalid and is announced with it. */
  error?: ReactNode | undefined;
  /** Positive validation message (e.g. "Username is available"). */
  success?: ReactNode | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  id?: string | undefined;
  /** `horizontal` = legacy MantisBT two-column form (label column on md+). */
  layout?: 'vertical' | 'horizontal' | undefined;
  className?: string | undefined;
}

/**
 * Label + control + hint + validation message. The message has an icon and text so validity is
 * never conveyed by colour alone (WCAG 1.4.1); required is marked visually with * and
 * programmatically with the `required` attribute on the control.
 */
export function FormField({
  label,
  children,
  hint,
  error,
  success,
  required = false,
  disabled = false,
  id,
  layout = 'vertical',
  className,
}: FormFieldProps) {
  const auto = useId();
  const controlId = id ?? `f${auto.replace(/:/g, '')}`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const messageId = error || success ? `${controlId}-msg` : undefined;
  const invalid = Boolean(error);
  const horizontal = layout === 'horizontal';

  return (
    <FieldContext.Provider
      value={{
        id: controlId,
        describedBy: [hintId, messageId].filter(Boolean).join(' ') || undefined,
        invalid,
        valid: !invalid && Boolean(success),
        required,
        disabled,
      }}
    >
      <div
        className={cx(
          'flex flex-col gap-1',
          horizontal && 'md:grid md:grid-cols-[minmax(8rem,1fr)_3fr] md:items-start md:gap-x-4',
          className,
        )}
        data-invalid={invalid || undefined}
      >
        <label
          htmlFor={controlId}
          className={cx(
            'text-sm font-semibold text-ink',
            horizontal && 'md:pt-1.5 md:text-right',
            disabled && 'opacity-60',
          )}
        >
          {label}
          {required && (
            <span aria-hidden="true" className="ms-0.5 text-danger-ink">
              *
            </span>
          )}
        </label>
        <div className="flex min-w-0 flex-col gap-1">
          {children}
          {hint && (
            <p id={hintId} className="text-xs text-ink-muted">
              {hint}
            </p>
          )}
          {error ? (
            <p id={messageId} className="flex items-start gap-1 text-xs text-danger-ink">
              <AlertCircle aria-hidden="true" className="mt-px size-3.5 shrink-0" />
              <span>{error}</span>
            </p>
          ) : success ? (
            <p id={messageId} className="flex items-start gap-1 text-xs text-success-ink">
              <CheckCircle2 aria-hidden="true" className="mt-px size-3.5 shrink-0" />
              <span>{success}</span>
            </p>
          ) : null}
        </div>
      </div>
    </FieldContext.Provider>
  );
}
